import React, { useState, useEffect } from 'react';
import { Check, TrendingUp, Info } from 'lucide-react';
import { 
  getAllDifficulties, 
  suggestDifficultyByAge,
  suggestDifficultyByPerformance,
  saveDifficultyPreference 
} from '../../utils/difficultyManager';
import { playSound } from '../../utils/soundManager';

const DifficultySelector = ({ 
  currentDifficulty, 
  onSelect, 
  userAge, 
  userProgress,
  showSuggestion = true 
}) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState(currentDifficulty);
  const [suggestedDifficulty, setSuggestedDifficulty] = useState(null);
  const difficulties = getAllDifficulties();

  useEffect(() => {
    // Suggest difficulty based on age and performance
    if (showSuggestion) {
      let suggested = currentDifficulty;
      
      if (userAge) {
        suggested = suggestDifficultyByAge(userAge);
      }
      
      if (userProgress && Object.keys(userProgress).length > 0) {
        suggested = suggestDifficultyByPerformance(userProgress, currentDifficulty);
      }
      
      if (suggested !== currentDifficulty) {
        setSuggestedDifficulty(suggested);
      }
    }
  }, [userAge, userProgress, currentDifficulty, showSuggestion]);

  const handleSelect = (difficulty) => {
    playSound('click');
    setSelectedDifficulty(difficulty.id);
    onSelect(difficulty.id);
  };

  return (
    <div className="difficulty-selector">
      <div className="difficulty-header">
        <h3>Choose Difficulty Level</h3>
        <p>Select the challenge that's right for you!</p>
      </div>

      {suggestedDifficulty && suggestedDifficulty !== currentDifficulty && (
        <div className="difficulty-suggestion animate-slideInDown">
          <TrendingUp size={20} />
          <span>
            Based on your performance, we suggest trying <strong>{suggestedDifficulty}</strong> level!
          </span>
        </div>
      )}

      <div className="difficulty-options">
        {difficulties.map((difficulty, index) => {
          const isSelected = selectedDifficulty === difficulty.id;
          const isSuggested = suggestedDifficulty === difficulty.id;

          return (
            <div
              key={difficulty.id}
              className={`difficulty-card hover-lift animate-scaleIn ${
                isSelected ? 'selected' : ''
              } ${isSuggested ? 'suggested' : ''}`}
              onClick={() => handleSelect(difficulty)}
              style={{
                animationDelay: `${index * 0.1}s`,
                borderColor: isSelected ? difficulty.color : 'var(--border-color)'
              }}
            >
              {isSuggested && (
                <div className="suggested-badge">
                  <Info size={14} />
                  Suggested
                </div>
              )}

              <div className="difficulty-icon" style={{ color: difficulty.color }}>
                <span className="difficulty-emoji">{difficulty.icon}</span>
              </div>

              <h4 style={{ color: difficulty.color }}>{difficulty.name}</h4>
              <p className="difficulty-label">{difficulty.label}</p>
              <p className="difficulty-description">{difficulty.description}</p>

              <div className="difficulty-details">
                <div className="difficulty-detail">
                  <span className="detail-label">Questions:</span>
                  <span className="detail-value">{difficulty.questionsPerQuiz}</span>
                </div>
                <div className="difficulty-detail">
                  <span className="detail-label">Pass Score:</span>
                  <span className="detail-value">{difficulty.passingScore}%</span>
                </div>
              </div>

              {isSelected && (
                <div className="selected-indicator" style={{ background: difficulty.color }}>
                  <Check size={20} color="white" />
                  Selected
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="difficulty-info">
        <Info size={16} />
        <span>You can change the difficulty level anytime from settings</span>
      </div>
    </div>
  );
};

export default DifficultySelector;
